import { createSelector } from '@reduxjs/toolkit';
import { isBefore } from 'date-fns';

import { PROJECT_STATUS, PROJECT_FILTER } from '../../interfaces/project';
import { AppState } from '../reducers/rootReducer';
import { ApplicationManagerInterface } from '../../interfaces/application';

export const selectAdminProjects = (state: AppState) => state.adminAppReducer.apps;
export const selectAdminProjectsDate = (state: AppState) => state.adminAppReducer.appsFetched;

export const selectAdminProjectById = (projectId?: string) =>
  createSelector(selectAdminProjects, (apps) => {
    if (!apps || apps.length <= 0 || !projectId) return null;
    return apps?.find((app) => app.application.info.applicationId === projectId);
  });

export const selectAdminCounts = createSelector(selectAdminProjects, (apps) => {
  if (!apps || apps.length === 0) return [0, 0];

  return apps.reduce(
    (a, v) => {
      const newAcc = [a[0], a[1]];
      if (v.application.setup?.status === PROJECT_STATUS.REVIEW) newAcc[0] += 1;
      if (v.application.setup?.status === PROJECT_STATUS.LIVE) newAcc[1] += 1;
      return newAcc;
    },
    [0, 0],
  );
});

const sortName = (a: ApplicationManagerInterface, b: ApplicationManagerInterface) =>
  a.application.info.name.toUpperCase() > b.application.info.name.toUpperCase() ? 1 : -1;

const sortId = (a: ApplicationManagerInterface, b: ApplicationManagerInterface) => {
  const id1 = a.application.info.applicationId;
  const id2 = b.application.info.applicationId;

  if (!id1 || !id2) return 0;

  return id1.toUpperCase() > id2.toUpperCase() ? 1 : -1;
};

const sortDate = (a: ApplicationManagerInterface, b: ApplicationManagerInterface) => {
  const date1 = a.application.lastUpdated;
  const date2 = b.application.lastUpdated;

  if (!date1 || !date2) return 0;

  return isBefore(new Date(date1), new Date(date2)) ? 1 : -1;
};

// TODO when sorting is clarified
const sortStatus = (a: ApplicationManagerInterface, b: ApplicationManagerInterface) => 0;

const getDisplayApps =
  (filter: PROJECT_FILTER, currentPage: number, rowsPerPage: number) =>
  (apps: ApplicationManagerInterface[] | undefined): [ApplicationManagerInterface[], number] => {
    if (!apps) return [[], 0];
    return [
      filterApps(filter, apps).slice(
        (currentPage - 1) * rowsPerPage,
        (currentPage - 1) * rowsPerPage + rowsPerPage,
      ),
      apps.length,
    ];
  };

const filterApps = (filter: PROJECT_FILTER, apps: ApplicationManagerInterface[]) => {
  switch (filter) {
    case PROJECT_FILTER.NAME:
      return [...apps].sort(sortName);
    case PROJECT_FILTER.ID:
      return [...apps].sort(sortId);
    case PROJECT_FILTER.UPDATED:
      return [...apps].sort(sortDate);
    case PROJECT_FILTER.STATUS:
      return [...apps].sort(sortStatus);
    default:
      return apps;
  }
};

export const selectReviewProjects = createSelector(selectAdminProjects, (apps) => {
  if (!apps) return [];
  return apps.filter((app) => app.application.setup.status === PROJECT_STATUS.REVIEW);
});

export const selectFilteredReviewProjects = (
  filter: PROJECT_FILTER,
  currentPage: number,
  rowsPerPage: number,
) => createSelector(selectReviewProjects, getDisplayApps(filter, currentPage, rowsPerPage));

export const selectAllFilteredProjects = (
  filter: PROJECT_FILTER,
  currentPage: number,
  rowsPerPage: number,
) => createSelector(selectAdminProjects, getDisplayApps(filter, currentPage, rowsPerPage));

export const selectProjectsByOrganisationId = (organisationId: number) =>
  createSelector(selectAdminProjects, (apps) => {
    if (!apps) return [];

    return apps
      .filter((app) => app.application.info.organisationId === organisationId)
      .map((p) => p.application);
  });

export const selectProjectOrganisationById = (organisationId: number) =>
  createSelector(selectAdminProjects, (apps) => {
    if (!apps) return undefined;

    return apps.find((app) => app.organisation.id === organisationId)?.organisation;
  });
